import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  addHandlerAction,
  addResponseAction,
  addTriggerAction,
  addTriggerCaseAction,
  conditionIdsByResponseIdSelector,
  getNextIdSelector,
  getNodeSlotIdSelector,
  handlersByIdSelector,
  removeHandlerAction,
  removeResponseAction,
  removeAllTriggersAction,
  removeTriggerAction,
  removeTriggerCaseAction,
  reorderHandlerAction,
  reorderResponseAction,
  reorderTriggerAction,
  responsesByIdSelector,
  triggerIdsStateSelector,
  triggersByIdSelector,
  updateHandlerAction,
  updateResponseAction,
  updateTriggerAction,
  updateTriggerCaseAction,
  type HandlerProperties,
  type ResponseProperties,
  type TriggerCase,
  type TriggerConditionBuild,
  type TriggerProperties,
} from "@skilitics/nautilus-core-designer-data";

import { resolveActiveNodesSelector } from "../nodes/querySelectors";
import {
  buildRuntimeConditionCreate,
  buildRuntimeEffectCreate,
  buildTriggerWhereConditionCreate,
  getDesignerNodeByIdSelector,
  getManifestSelector,
  getParentContextByIdSelector,
  getRuntimeTriggersCreate,
  getTriggerEventsCreate,
} from "../state";
import { runtimeTriggersDataSelector } from "../state/selectors";
import type { ConditionBuildable, EffectBuildable } from "../types";
import { resolveValueToConditionValueSelector, resolveValueToRuntimeDynamicValueSelector } from "../values";
import { getConditionBuildableCreate } from "./conditions";
import { getEffectBuildableCreate } from "./effects";
import {
  getNodeConditionBuildablesCreate,
  getNodeEffectBuildablesCreate,
  resolveConditionBuildablesCreate,
  resolveEffectBuildablesCreate,
} from "./querying";
import { TriggerDesignsContext, type ResponseDetails } from "./TriggerDesignsContext";

type Props = {
  /** generally available builders (not node specific) e.g. group, negate */
  utilityConditionBuildables: ReadonlyArray<ConditionBuildable>;
  /** generally available effect builders (not node specific) e.g. locale */
  coreEffectBuildables: ReadonlyArray<Omit<EffectBuildable, "nodeId">>;
  children: Exclude<ReactNode, undefined | null | boolean>;
};

// TODO: lots more selector migration to come here
export const TriggerDesignsProvider = ({ utilityConditionBuildables, coreEffectBuildables, children }: Props) => {
  const dispatch = useDispatch();
  const getById = useSelector(getDesignerNodeByIdSelector);
  const getParentContextById = useSelector(getParentContextByIdSelector);
  const getSlotId = useSelector(getNodeSlotIdSelector);
  const resolveActiveNodes = useSelector(resolveActiveNodesSelector);
  const getManifest = useSelector(getManifestSelector);
  const getNextId = useSelector(getNextIdSelector);
  const responsesById = useSelector(responsesByIdSelector);
  const handlersById = useSelector(handlersByIdSelector);
  const conditionIdsByResponseId = useSelector(conditionIdsByResponseIdSelector);
  const triggerIdsState = useSelector(triggerIdsStateSelector);
  const triggersById = useSelector(triggersByIdSelector);

  const getNodeEffectBuildables = useMemo(
    () => getNodeEffectBuildablesCreate({ getManifest, getParentContextById }),
    [getManifest, getParentContextById],
  );

  const getConditionBuildables = useMemo(
    () => getNodeConditionBuildablesCreate(getManifest, getParentContextById),
    [getManifest, getParentContextById],
  );

  const resolveConditionValue = useSelector(resolveValueToConditionValueSelector);
  const resolveRuntimeValue = useSelector(resolveValueToRuntimeDynamicValueSelector);

  const resolveEffectBuildables = useMemo(
    () =>
      resolveEffectBuildablesCreate({
        resolveActiveNodes,
        getNodeEffectBuildables,
        getById,
        getSlotId,
        coreBuildables: coreEffectBuildables,
      }),
    [resolveActiveNodes, getNodeEffectBuildables, getById, getSlotId, coreEffectBuildables],
  );

  const resolveConditionBuildables = useMemo(
    () =>
      resolveConditionBuildablesCreate(resolveActiveNodes, getConditionBuildables, getById, utilityConditionBuildables),
    [resolveActiveNodes, getConditionBuildables, getById, utilityConditionBuildables],
  );

  const addTrigger = useCallback(
    (nodeId: number, eventType: string, where?: TriggerConditionBuild) => {
      const triggerId = getNextId();
      dispatch(addTriggerAction({ nodeId, id: triggerId, cases: [{ eventType, where }] }));
      const handlerId = getNextId();
      dispatch(addHandlerAction({ triggerId, id: handlerId }));
      const responseId = getNextId();
      dispatch(addResponseAction({ handlerId, id: responseId }));
      return { triggerId, handlerId, responseId };
    },
    [dispatch, getNextId],
  );

  const addHandler = useCallback(
    (triggerId: number): number => {
      const handlerId = getNextId();
      dispatch(addHandlerAction({ triggerId, id: handlerId }));
      const responseId = getNextId();
      dispatch(addResponseAction({ handlerId, id: responseId }));
      return handlerId;
    },
    [dispatch, getNextId],
  );

  const getHandler = useCallback(
    (id: number) => {
      const handler = handlersById[id];
      if (!handler) {
        throw new Error(`Handler with id ${id} not found.`);
      }
      return { ...handler, id };
    },
    [handlersById],
  );

  const getHandlers = useCallback(
    (triggerId: number) => {
      const handlerIds = triggerIdsState.handlerIdsByTriggerId[triggerId] ?? [];
      return handlerIds.map(getHandler);
    },
    [getHandler, triggerIdsState],
  );

  const updateHandler = useCallback(
    (id: number, props: Partial<HandlerProperties>) => {
      dispatch(updateHandlerAction({ id, ...props }));
    },
    [dispatch],
  );

  const removeHandler = useCallback(
    (id: number) => {
      dispatch(removeHandlerAction({ id }));
    },
    [dispatch],
  );

  const reorderHandler = useCallback(
    (id: number, newIndex: number) => {
      dispatch(reorderHandlerAction({ id, newIndex }));
    },
    [dispatch],
  );

  const addResponse = useCallback(
    (handlerId: number): number => {
      const responseId = getNextId();
      dispatch(addResponseAction({ handlerId, id: responseId }));
      return responseId;
    },
    [dispatch, getNextId],
  );

  const removeResponse = useCallback(
    (id: number) => {
      dispatch(removeResponseAction({ id }));
    },
    [dispatch],
  );

  const getResponse = useCallback(
    (id: number): ResponseDetails => {
      const response = responsesById[id];
      if (!response) {
        throw new Error(`Response with id ${id} not found.`);
      }
      const conditionId = conditionIdsByResponseId[id];
      const catchAll = !conditionId; // no root condition means catch all
      return { ...response, id, catchAll };
    },
    [conditionIdsByResponseId, responsesById],
  );

  const getResponses = useCallback(
    (handlerId: number) => {
      const responseIds = triggerIdsState.responseIdsByHandlerId[handlerId] ?? [];
      return responseIds.map(getResponse);
    },
    [getResponse, triggerIdsState],
  );

  const updateResponse = useCallback(
    (id: number, props: ResponseProperties) => {
      dispatch(updateResponseAction({ id, ...props }));
    },
    [dispatch],
  );

  const reorderResponse = useCallback(
    (id: number, newIndex: number) => {
      dispatch(reorderResponseAction({ id, newIndex }));
    },
    [dispatch],
  );

  const getTriggersForEventType = useCallback(
    (nodeId: number, eventType: string) => {
      const triggerIds = triggerIdsState.triggerIdsByNodeId[nodeId];
      if (!triggerIds) {
        return [];
      }
      const triggerIdsForEventType = triggerIds.filter((id) =>
        triggersById[id]?.cases.some((c) => c.eventType === eventType),
      );
      return triggerIdsForEventType.map((id) => ({ ...triggersById[id], id }));
    },
    [triggerIdsState.triggerIdsByNodeId, triggersById],
  );

  const getTrigger = useCallback(
    (id: number) => {
      const trigger = triggersById[id];
      if (!trigger) {
        throw new Error(`Trigger with id ${id} not found.`);
      }
      return { ...trigger, id: id };
    },
    [triggersById],
  );

  const updateTrigger = useCallback(
    (id: number, props: Partial<TriggerProperties>) => {
      dispatch(updateTriggerAction({ id, ...props }));
    },
    [dispatch],
  );

  const addTriggerCase = useCallback(
    (id: number, triggerCase: TriggerCase) => {
      dispatch(addTriggerCaseAction({ id, ...triggerCase }));
    },
    [dispatch],
  );

  const updateTriggerCase = useCallback(
    (id: number, index: number, updates: Partial<TriggerCase>) => {
      dispatch(updateTriggerCaseAction({ id, index, ...updates }));
    },
    [dispatch],
  );

  const removeTriggerCase = useCallback(
    (id: number, index: number) => {
      dispatch(removeTriggerCaseAction({ id, index }));
    },
    [dispatch],
  );

  const reorderTrigger = useCallback(
    (id: number, newIndex: number) => {
      dispatch(reorderTriggerAction({ id, newIndex }));
    },
    [dispatch],
  );

  const removeTrigger = useCallback(
    (id: number) => {
      dispatch(removeTriggerAction({ id }));
    },
    [dispatch],
  );

  const removeAllTriggers = useCallback(
    (nodeId: number) => {
      dispatch(removeAllTriggersAction({ nodeId }));
    },
    [dispatch],
  );

  const getNodeIdByTriggerId = useCallback(
    (triggerId: number) => {
      const nodeId = triggerIdsState.nodeIdByTriggerId[triggerId];
      if (!nodeId) {
        throw new Error(`nodeId not found with triggerId: ${triggerId}`);
      }
      return nodeId;
    },
    [triggerIdsState],
  );

  const getHandlerId = useCallback(
    (responseId: number) => {
      const handlerId = triggerIdsState.handlerIdByResponseId[responseId];
      if (!handlerId) {
        throw new Error(`Handler not found with responseId: ${responseId}`);
      }
      return handlerId;
    },
    [triggerIdsState],
  );

  const getTriggerId = useCallback(
    (responseId: number) => {
      const handlerId = triggerIdsState.handlerIdByResponseId[responseId];
      if (!handlerId) {
        throw new Error(`Handler not found with responseId: ${responseId}`);
      }
      const triggerId = triggerIdsState.triggerIdByHandlerId[handlerId];
      if (!triggerId) {
        throw new Error(`Trigger not found with handlerId: ${handlerId}`);
      }
      return triggerId;
    },
    [triggerIdsState],
  );

  const getConditionBuildable = useMemo(
    () => getConditionBuildableCreate(getConditionBuildables, getById, utilityConditionBuildables),
    [getConditionBuildables, getById, utilityConditionBuildables],
  );

  const runtimeTriggersData = useSelector(runtimeTriggersDataSelector);

  const getTriggerEvents = useMemo(
    () => getTriggerEventsCreate({ getManifest, getNodeById: getById }),
    [getById, getManifest],
  );

  const getRuntimeTriggers = useMemo(() => {
    const getEffectBuildable = getEffectBuildableCreate(getNodeEffectBuildables, getById, coreEffectBuildables);
    return getRuntimeTriggersCreate({
      getEffectBuildable,
      buildRuntimeEffect: buildRuntimeEffectCreate(getEffectBuildable, resolveRuntimeValue),
      buildRuntimeCondition: buildRuntimeConditionCreate(getConditionBuildable, resolveConditionValue),
      buildTriggerWhereCondition: buildTriggerWhereConditionCreate(getTriggerEvents),
      ...runtimeTriggersData,
    });
  }, [
    coreEffectBuildables,
    getById,
    getConditionBuildable,
    getNodeEffectBuildables,
    getTriggerEvents,
    resolveConditionValue,
    resolveRuntimeValue,
    runtimeTriggersData,
  ]);

  const getHaveTriggers = useCallback(
    (nodeId: number) => {
      const triggerIds = triggerIdsState.triggerIdsByNodeId[nodeId];
      return triggerIds && triggerIds.length > 0;
    },
    [triggerIdsState],
  );

  const getCustomComponent: TriggerDesignsContext["getCustomComponent"] = useMemo(
    () => (nodeOrId) => {
      const node = typeof nodeOrId === "number" ? getById(nodeOrId) : nodeOrId;
      const { designer } = getManifest(node.typeId);
      return designer.triggersComponent;
    },
    [getById, getManifest],
  );

  const value = useMemo(
    () => ({
      addTrigger,
      getTriggersForEventType,
      getHaveTriggers,
      getTrigger,
      updateTrigger,
      reorderTrigger,
      removeTrigger,
      removeAllTriggers,
      addTriggerCase,
      updateTriggerCase,
      removeTriggerCase,
      getNodeIdByTriggerId,

      addHandler,
      getHandler,
      getHandlers,
      updateHandler,
      removeHandler,
      reorderHandler,
      getHandlerId,
      getTriggerId,

      addResponse,
      removeResponse,
      getResponse,
      getResponses,
      updateResponse,
      reorderResponse,

      getRuntimeTriggers,
      getTriggerEvents,

      getEffectBuildables: getNodeEffectBuildables,
      getConditionBuildables,
      getConditionBuildable,
      resolveEffectBuildables,
      resolveConditionBuildables,

      getCustomComponent,
    }),
    [
      addHandler,
      addResponse,
      addTrigger,
      addTriggerCase,
      getConditionBuildable,
      getConditionBuildables,
      getCustomComponent,
      getHandler,
      getHandlerId,
      getHandlers,
      getHaveTriggers,
      getNodeEffectBuildables,
      getNodeIdByTriggerId,
      getResponse,
      getTriggerId,
      getResponses,
      getRuntimeTriggers,
      getTrigger,
      getTriggerEvents,
      getTriggersForEventType,
      removeHandler,
      removeResponse,
      removeTrigger,
      removeAllTriggers,
      removeTriggerCase,
      reorderHandler,
      reorderResponse,
      reorderTrigger,
      resolveConditionBuildables,
      resolveEffectBuildables,
      updateHandler,
      updateResponse,
      updateTrigger,
      updateTriggerCase,
    ],
  );

  return <TriggerDesignsContext.Provider value={value}>{children}</TriggerDesignsContext.Provider>;
};
